import { inject, Injectable } from '@angular/core';
import {
  BookingCalculationService,
  getEarlyBirdPricingRuleBracket,
  getTicketTier,
  IBookingCalculationData,
  ICalcBookingAmounts,
  ICalcOrderItem,
  ICalculationOptions
} from '@roller/booking-calculation-service';
import { DateFormatService } from '@core/date/date-format.service';
import { VenueConfigService } from '@core/venue-config/venue-config.service';
import { IProductInfo } from './models/product-info.model';
import { IProduct } from './models/product.model';
import { CEmptyMinProductCost, IMinProductCost } from './models/min-product-cost.model';
import { CSessionBasedProductTypes, TProductType } from './models/product-type.type';
import { ISessionDiscount } from './models/product-session-discount.model';
import { IProductSession } from './models/product-session.model';
import { ProductHelperService } from './product-helper.service';
import { rdlIsNumber } from '@roller/rdl/utilities/number';

@Injectable({
  providedIn: 'root'
})
export class ProductPricingService {
  private calculationService = inject(BookingCalculationService);
  private dateFormatService = inject(DateFormatService);
  private venueConfigService = inject(VenueConfigService);
  private productHelperService = inject(ProductHelperService);

  /**
   * Calculates the price to display to the user for a product
   * This always takes into consideration pricing rules, time tiers, and early bird brackets
   * If drip pricing transparency is enabled, this will also include all fees and taxes that the guest will pay on this product
   * @param product The product info for additional details
   * @param bookingDate The date of the booking
   * @param sessionStart The start time of the session
   * @param bookingCreatedDate The date the booking was created
   * @returns The display price of the product
   */
  getDisplayPrice(product: IProductInfo, bookingDate?: Date, sessionStart?: string, bookingCreatedDate?: Date): number {
    const basePrice = this.getBasePrice(product, bookingDate, sessionStart, bookingCreatedDate);

    return this.getDisplayPriceFromBasePrice(basePrice, product);
  }

  /**
   * Returns the base price of a product, considering pricing rules, time tiers, early bird brackets and consecutive session discounts
   * @param product The product info
   * @param bookingDate The date of the booking
   * @param sessionStart The start time of the session
   * @param bookingCreatedDate The date the booking was created
   * @returns The base price of the product
   */
  getBasePrice(product: IProductInfo, bookingDate?: Date, sessionStart?: string, bookingCreatedDate?: Date): number {
    if (product.pricingRuleBracket) {
      return product.pricingRuleBracket.price;
    }

    if (product.pricingRule?.timeRule && product.pricingRule.timeTiers && bookingDate) {
      const sessionStartTime = sessionStart ? this.dateFormatService.hourStringToNumber(sessionStart) : undefined;
      const tier = getTicketTier(
        product.pricingRule.timeTiers,
        product.pricingRule.timeRule,
        bookingDate,
        sessionStartTime
      );

      return tier?.price ?? product.cost;
    }

    if (product.pricingRule?.earlyBirdBrackets && bookingDate) {
      const timezoneOffset: number = this.venueConfigService.getVenueConfig().venueSettings.timeZoneOffsetMinutes ?? 0;
      const currentVenueTime = this.dateFormatService.currentVenueTime(timezoneOffset);
      const earlyBirdBracket = getEarlyBirdPricingRuleBracket(
        bookingDate,
        product.pricingRule.earlyBirdBrackets,
        currentVenueTime,
        bookingCreatedDate
      );
      return earlyBirdBracket.price ?? product.cost;
    }

    return product.appliedSessionDiscount?.computedCost ?? product.cost;
  }

  /**
   * Find the lowest product in the current product - e.g. child ticket.
   * * Return 0 if not food and beverage and giftcards, wallets, addon, custom, recurringPass (later), cashlesscard
   * * If membership upgrade then exclude products with lower cost
   * * For products with a minimum ticket purchase, the minimum total will be display
   * * If a ticket cost is 0 and there are higher values - exclude the 0
   * * If all else fails return 0
   * * If there is only one ticket don't display the From
   * * If there is a forced min purchase return the min cost of package.
   * * If there is an early bird price rule, return the min cost based on the price rule bracket for the selected date
   * * UX `product-item.component.html` - will no longer show if it is 0 (falsy)
   * @returns the lowest cost item in the product products cost along with descriptor for key to use.
   */
  getMinProductCost(parentProduct: IProduct, bookingDate?: Date): IMinProductCost {
    const isFoodAndBev = this.venueConfigService.isFoodAndBeverageCheckout();
    const noFromPrice: TProductType[] = isFoodAndBev
      ? []
      : ['giftcard', 'wallet', 'addon', 'custom', 'recurringpass', 'cashlesscard'];

    if (!parentProduct.type || noFromPrice.includes(parentProduct.type)) {
      return CEmptyMinProductCost;
    }

    const venueConfig = this.venueConfigService.getVenueConfig();
    const timezoneOffset = venueConfig.venueSettings.timeZoneOffsetMinutes ?? 0;
    const currentVenueTime = this.dateFormatService.currentVenueTime(timezoneOffset);
    const minList: number[] = [];
    const productInfoList: IProductInfo[] = [];
    const priceRuleMinList: number[] = [];
    const earlyBirdPrices: number[] = [];

    // safety check for unit tests or malformed typecast as product
    if (Object.keys(parentProduct).length > 0) {
      // A product could have a number of forced min purchase tickets, therefore this adds to the base cost of every ticket
      const packageForcedCost = parentProduct.products.reduce((val: number, ticket: IProductInfo) => {
        if (ticket.forceMinPurchase) {
          return val + ticket.cost * ticket.minPurchase;
        }

        return val;
      }, 0);
      parentProduct.products.forEach((childProduct: IProductInfo) => {
        // Skip tickets that have no available sessions for session-based products
        if (parentProduct.sessions && CSessionBasedProductTypes.includes(parentProduct.type as TProductType)) {
          const hasAvailableSessions = parentProduct.sessions.some((session: IProductSession) =>
            this.productHelperService.isSessionAvailable(session, childProduct.minPurchase, false, childProduct.id)
          );
          if (!hasAvailableSessions) {
            return;
          }
        }

        let cost = childProduct.cost;

        if (childProduct.hasUserDefinedCost && childProduct.minUserDefinedCost) {
          cost = childProduct.minUserDefinedCost;
        }

        cost = childProduct.forceMinPurchase ? packageForcedCost : cost * childProduct.minPurchase + packageForcedCost;
        cost = this.getDisplayPriceFromBasePrice(cost, childProduct);

        if (cost) {
          minList.push(cost);
          productInfoList.push(childProduct);

          childProduct.sessionDiscounts?.forEach((consecutiveSession: ISessionDiscount) => {
            let consecutiveSessionCost = this.getConsecutiveSessionTicketCost(cost, consecutiveSession);
            consecutiveSessionCost = this.getDisplayPriceFromBasePrice(consecutiveSessionCost, childProduct);
            if (consecutiveSessionCost) {
              minList.push(consecutiveSessionCost);
              productInfoList.push(childProduct);
            }
          });
        }

        if (childProduct.pricingRule?.timeTiers && childProduct.pricingRule?.timeRule && bookingDate) {
          const timeTier = getTicketTier(
            childProduct.pricingRule.timeTiers,
            childProduct.pricingRule.timeRule,
            bookingDate
          );
          const tierPrice = this.getDisplayPriceFromBasePrice(timeTier.price, childProduct);
          priceRuleMinList.push(tierPrice);
        }

        if (childProduct.pricingRule?.earlyBirdBrackets && bookingDate && currentVenueTime) {
          const earlyBirdBracket = getEarlyBirdPricingRuleBracket(
            bookingDate,
            childProduct.pricingRule.earlyBirdBrackets,
            currentVenueTime
          );

          if (earlyBirdBracket.price) {
            const earlyBirdPrice = this.getDisplayPriceFromBasePrice(earlyBirdBracket.price, childProduct);
            earlyBirdPrices.push(earlyBirdPrice);
          }
        }
      });
    }

    if (!minList.length) {
      return CEmptyMinProductCost;
    }

    let minCost = Math.min(...minList);
    const minIndex = minList.indexOf(minCost);
    const cheapestProduct = productInfoList[minIndex];

    const translationKey =
      minList.length > 1 || priceRuleMinList.length > 1 || earlyBirdPrices.length > 1
        ? 'GENERIC.FROM'
        : 'GENERIC.PRICE';
    const recurring = cheapestProduct.recurringPayment;

    if (recurring) {
      return { minCost, translationKey, recurring };
    }

    if (priceRuleMinList.length) {
      minCost = Math.min(...priceRuleMinList);
    }

    if (earlyBirdPrices.length > 0) {
      const minEarlyBirdPrice = Math.min(...earlyBirdPrices);
      minCost = Math.min(minCost, minEarlyBirdPrice);
    }

    if (this.productHelperService.isUserDefinedGroupSize(productInfoList[minIndex])) {
      const guestPerQuantity = this.productHelperService.getGuestPerQuantity(productInfoList[minIndex]);
      return { minCost, translationKey, guestPerQuantity };
    }

    return { minCost, translationKey };
  }

  /**
   * Gets the cost for a ticket based on a consecutive session discount
   * @param ticketCost the base cost of the ticket
   * @param sessionDiscount the consecutive session discount to apply
   * @returns the cost of the ticket modified by the number of sessions and any discount
   */
  getConsecutiveSessionTicketCost(ticketCost: number, sessionDiscount: ISessionDiscount): number {
    let costBeforeDiscount = ticketCost * sessionDiscount.numberOfSessions;
    if (sessionDiscount.amountOff) {
      costBeforeDiscount -= sessionDiscount.amountOff;
    } else if (sessionDiscount.percentOff) {
      costBeforeDiscount -= (costBeforeDiscount * sessionDiscount.percentOff) / 100;
    }

    return costBeforeDiscount;
  }

  /**
   * Gets the display price from the base price, applying drip pricing transparency if enabled.
   * Use this when you have a static base price to run through the check.
   * @param basePrice The base price of the product
   * @param product The product info
   * @returns The display price including all fees and taxes
   */
  getDisplayPriceFromBasePrice(basePrice: number, product: IProductInfo): number {
    if (this.venueConfigService.isDripPricingTransparencyEnabled()) {
      return this.getProductAmounts(basePrice, product).total;
    }

    return basePrice + this.getTaxModifierTotal(product);
  }

  /**
   * Gets the item tax modifiers total
   * @param product The product info
   * @returns The item tax modifiers total
   */
  getTaxModifierTotal(product: IProductInfo): number {
    return (
      product.bookingModifiers
        ?.filter((modifier) => !!modifier.quantity && !!modifier.amount && rdlIsNumber(modifier.taxPercent))
        .reduce((total, taxModifier) => total + taxModifier.quantity! * taxModifier.amount!, 0) ?? 0
    );
  }

  /**
   * Get the amounts for a product when drip pricing transparency is enabled
   * @param product The product info
   * @returns The amounts
   */
  getDripPricingTransparencyAmounts(
    product: IProductInfo,
    bookingDate?: Date,
    sessionStart?: string,
    bookingCreatedDate?: Date
  ): ICalcBookingAmounts {
    const basePrice = this.getBasePrice(product, bookingDate, sessionStart, bookingCreatedDate);

    return this.getProductAmounts(basePrice, product);
  }

  /**
   * Get the amounts for a product when drip pricing transparency is enabled
   * @param basePrice The base price of the product
   * @param product The product info
   * @returns The amounts
   */
  private getProductAmounts(basePrice: number, product: IProductInfo): ICalcBookingAmounts {
    const venueConfig = this.venueConfigService.getVenueConfig();

    // Pass a mocked one-item order to the calculation service to get the amounts
    const fakeCalcOrderItem: ICalcOrderItem = {
      cost: basePrice,
      quantity: 1,
      tax: product.tax,
      isTaxInclusive: product.isTaxInclusive,
      perProductBookingFee: product.perProductBookingFee ?? product.fee,
      giftCardPostageItems: [],
      productId: product.id,
      parentProductId: product.parentProductId ?? 'mock-parent-id',
      groupSize: product.groupSize,
      pricingRule: product.pricingRule,
      // Only include sessionDiscount if the basePrice doesn't already include the consecutive session calculation
      // If appliedSessionDiscount.computedCost exists, it means basePrice already includes the consecutive session calculation
      sessionDiscount: product.appliedSessionDiscount?.computedCost ? undefined : product.appliedSessionDiscount,
      costPercentageRequired: 0 // No deposit for display price calculation,
    };

    const bookingCalculationData: IBookingCalculationData = {
      items: [fakeCalcOrderItem],
      discountResult: [],
      giftCardResult: [],
      costSettings: {
        ...venueConfig.costSettings,
        feePerTransaction: 0
      },
      tip: 0,
      paid: 0
    };

    const calculationOptions: ICalculationOptions = {
      considerDeposit: false, // We want the full price, not deposit
      guestPayFees: venueConfig.paymentPage?.guestPayFees ?? false
    };

    return this.calculationService.getAmounts(bookingCalculationData, calculationOptions);
  }
}
